import React, {useState} from 'react'
import { Link, useLocation } from 'react-router-dom'
import imgLogo from '../../../assets/images/ic-logo-black.png'
import {isFirebaseAuthEnabled} from '../../firebase/firebaseClient'
import {FirebaseAuthService} from '../../firebase/FirebaseAuthService'
import {postForgotPassword} from '../../global/api'
import styles from '../auth/AuthPage.module.scss'

function ForgotPassword() {
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('Oops! Something went wrong. Please try again.')
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [hasError, setHasError] = useState(false)

  const isValidEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value)

  const handleChangeEmail = ({ target: { value } }: { target: { value: any } }) => {
    setEmail(value)
    setFieldError('')
    setHasError(false)
    setIsEmailSent(false)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setFieldError('Please enter your email address.')
      return
    }

    if (!isValidEmail(email.trim())) {
      setFieldError('Please enter a valid email address.')
      return
    }

    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    try {
      if (isFirebaseAuthEnabled()) {
        // The reset email links back to the sign-in page with the same OAuth request.
        await FirebaseAuthService.sendPasswordReset(email, `${window.location.origin}/${location.search}`)
      } else {
        await postForgotPassword(email)
      }
      onShowSuccess()
    } catch (apiError) {
      onShowError(apiError instanceof Error ? apiError.message : (apiError as string))
    } finally {
      setIsSubmitting(false)
    }
  }

  const onShowSuccess = () => {
    setIsEmailSent(true)
    setHasError(false)
  }

  const onShowError = (errorMessage: string) => {
    if (errorMessage) {
      setErrorMessage(errorMessage)
    }
    setHasError(true)
    setIsEmailSent(false)
  }

  return (
    <main className={styles.authPage}>
      <section className={styles.authShell} aria-label='Password reset'>
        <div className={styles.card}>
          <img className={styles.logo} src={imgLogo} alt='Awair logo' />
          <p className={styles.eyebrow}>Awair Account</p>
          <h1 className={styles.title}>Reset password</h1>
          <p className={styles.subtitle}>Enter your account email and we will send a reset link.</p>

          <form className={styles.form} onSubmit={onSubmit} noValidate>
            <div>
              <label htmlFor='user-email' className={styles.fieldLabel}>Email address</label>
              <input
                type='email'
                className={`${styles.input} ${fieldError ? styles.inputError : ''}`}
                id='user-email'
                name='user_email'
                placeholder='name@example.com'
                value={email}
                onChange={handleChangeEmail}
                autoComplete='email'
                aria-invalid={Boolean(fieldError)}
              />
            </div>

            <button
              className={styles.submitButton}
              type='submit'
              id='submit-button'
              name='submit'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending link...' : 'Send reset link'}
            </button>

            {fieldError ? (
              <p className={`${styles.status} ${styles.statusError}`} role='alert'>
                {fieldError}
              </p>
            ) : null}

            {isEmailSent ? (
              <p className={`${styles.status} ${styles.statusSuccess}`} role='status' aria-live='polite'>
                Email sent. Please check your inbox.
              </p>
            ) : null}

            {hasError ? (
              <p className={`${styles.status} ${styles.statusError}`} role='alert' aria-live='assertive'>
                {errorMessage}
              </p>
            ) : null}

            <div className={styles.helperRow}>
              <Link to={{ pathname: '/', search: location.search }} className={styles.link}>Back to sign in</Link>
            </div>
          </form>

          <p className={styles.footerNote}>If you do not receive an email, verify your address and try again.</p>
          </div>
        </section>
    </main>
  )
}

export default ForgotPassword
